import { Decorative } from "@faire/design-tokens";
import { logError } from "@faire/web--source/common/logging";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { QueryParameters as PostSampleQueryParams } from "@faire/web-api--source/api/retailer/sample/prospected-sample-request/brand/brandToken/post";
import { useMutation as usePostSampleRequest } from "@faire/web-api--source/api/retailer/sample/prospected-sample-request/brand/brandToken/post-hooks";
import { trackSamplesGuestRetailerConfirmationModalView } from "@faire/web-api--source/events/samples/view/guestRetailerConfirmationModal";
import { trackSamplesShippingDetailsModalView } from "@faire/web-api--source/events/samples/view/shippingDetailsModal";
import { ISubmitProspectedSampleRequestFormRequest } from "@faire/web-api--source/faire/samples/ISubmitProspectedSampleRequestFormRequest";
import { IAddress } from "@faire/web-api--source/indigofair/data/IAddress";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import { IMatchedBusiness } from "@faire/web-api--source/indigofair/data/IMatchedBusiness";
import { QueryParameters as SignUpQueryParameters } from "@faire/web-api--source/routes/direct/brandAlias";
import { WebApiError } from "@faire/web-api--source/WebApiError";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { SAMPLE_PROSPECTING_SAMPLE_REQUEST_COMPLETE_VALUE } from "@faire/retailer/components/App/AppModals/consts";
import { ImageOrLogo } from "@faire/retailer/components/AppSignUp/components/ImageOrLogo";
import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { getSessionCountry } from "@faire/retailer/serialized-data/getSessionCountry";

import { GuestRetailerConfirmationModal } from "./GuestRetailerConfirmationModal";
import { ShippingDetails } from "./ShippingDetails";

interface IProps {
  brand: IBrand;
  email: string;
}

enum ModalState {
  SHIPPING_DETAILS,
  CONFIRMATION,
}

export interface SampleForm {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  storeName: string;
  optIntoEmailNotifications: boolean;
  matchedBusiness: IMatchedBusiness;
}

export const ProspectingSampleSignupFlow: React.FC<IProps> = ({
  brand,
  email,
}) => {
  const sessionCountry = getSessionCountry();
  const [modalState, setModalState] = useState(ModalState.SHIPPING_DETAILS);
  const [formValues, setFormValues] = useState<SampleForm>({
    email,
    firstName: "",
    lastName: "",
    phoneNumber: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    storeName: "",
    country: sessionCountry ?? "",
    optIntoEmailNotifications: true,
    matchedBusiness: IMatchedBusiness.build(),
  });
  const { push } = useHistory();
  const { faireDirectBrandToken } = useFaireDirectBrand();
  const { sampleInvite } = useQueryParams(SignUpQueryParameters.parse);
  const {
    mutate: postSampleRequest,
    isError,
    error,
    isPending: isLoading,
  } = usePostSampleRequest();

  useEffect(() => {
    setFormValues({
      email,
      firstName: "",
      lastName: "",
      phoneNumber: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipcode: "",
      storeName: "",
      country: sessionCountry ?? "",
      optIntoEmailNotifications: true,
      matchedBusiness: IMatchedBusiness.build(),
    });
  }, [email, sessionCountry]);

  const redirectToLandingPage = () => {
    push(
      getCurrentPath()
        .delete("email")
        .replace({
          signUp: SAMPLE_PROSPECTING_SAMPLE_REQUEST_COMPLETE_VALUE,
        })
        .toString()
    );
  };

  const trackSamplesShippingDetailsModalViewRef = useDoActionWhenVisible(() => {
    trackSamplesShippingDetailsModalView();
  });
  const trackSamplesGuestRetailerConfirmationModalViewRef =
    useDoActionWhenVisible(() => {
      trackSamplesGuestRetailerConfirmationModalView();
    });

  return (
    <Container data-test-id="prospecting-sample-signup">
      <LogoXFaire>
        <ImageOrLogo logo image={brand.profile_image} />
      </LogoXFaire>
      {modalState === ModalState.SHIPPING_DETAILS ? (
        <FormWrapper ref={trackSamplesShippingDetailsModalViewRef}>
          <ShippingDetails
            initialValues={formValues}
            setFormValues={setFormValues}
            onSubmit={(values, { setSubmitting }) =>
              postSampleRequest(
                [
                  faireDirectBrandToken,
                  ISubmitProspectedSampleRequestFormRequest.build({
                    email_address: values.email,
                    first_name: values.firstName,
                    last_name: values.lastName,
                    destination_address: IAddress.build({
                      name: values.firstName + " " + values.lastName,
                      address1: values.address1,
                      address2: values.address2,
                      city: values.city,
                      state: values.state,
                      postal_code: values.zipcode,
                      country: values.country,
                      phone_number: values.phoneNumber,
                    }),
                    opt_into_email_notifications:
                      values.optIntoEmailNotifications,
                    matched_business: values.matchedBusiness,
                  }),
                  PostSampleQueryParams.build({
                    invite_token: sampleInvite,
                  }),
                ],
                {
                  onSuccess: () => {
                    setModalState(ModalState.CONFIRMATION);
                    setSubmitting(false);
                  },
                  onError: (error) => {
                    logError(error);
                    setSubmitting(false);
                  },
                }
              )
            }
            apiCallFailed={isError}
            apiCallIsLoading={isLoading}
            apiCallError={error as WebApiError}
          />
        </FormWrapper>
      ) : null}
      {modalState === ModalState.CONFIRMATION ? (
        <GuestRetailerConfirmationModal
          bannerImage={brand.banner_image}
          name={`${formValues.firstName} ${formValues.lastName}`}
          email={formValues.email}
          storeName={formValues.storeName}
          country={formValues.country}
          googlePlaceId={formValues.matchedBusiness.id ?? ""}
          phoneNumber={formValues.phoneNumber}
          onLearnMoreClick={redirectToLandingPage}
          ref={trackSamplesGuestRetailerConfirmationModalViewRef}
        />
      ) : null}
    </Container>
  );
};

export const Container = styled(Column).attrs({
  justify: "center",
  align: "center",
  gap: "3x",
})`
  background: ${Decorative.tint.neutral};
  width: 100%;
  padding: 40px;
  height: auto;
  min-height: 100vh;
`;

export const FormWrapper = styled(Column).attrs({
  gap: "3x",
  justify: "center",
  align: "center",
})``;

const LogoXFaire = styled(Row).attrs({ justify: "center" })``;
